import {Component, inject, NgZone, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import LogRocket from 'logrocket';
import {RecipientUser, SessionUser} from '@gatewee/types/user';
import {MobileBridgeService} from '@gatewee/services/core/mobile-bridge.service';
import {readProfileRaw} from '@gatewee/services';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit {
  private translate = inject(TranslateService);
  private zone = inject(NgZone);

  constructor(private bridge: MobileBridgeService) {
    this.setupLanguage();
    this.setSessionUser(JSON.parse(readProfileRaw()));
    // this.logRocketInit();
  }

  ngOnInit(): void {
    this.bridge.start();
    this.installIosInputFocusFix();
    this.logRocketInit();
  }

  /**
   * iOS Safari workaround for Taiga UI pickers and inputs inside
   * `<tui-textfield>` / `<tui-textarea>`. Three cases are handled on a
   * direct tap of the inner `<input>` or `<textarea>` element:
   *
   * 1. **Select-like** (`readonly` or `inputmode="none"`, e.g. `tuiSelect`,
   *    `gatewee-select-v3`, `gatewee-multi-select`): iOS skips the synthetic
   *    `click` for readonly inputs nested in custom elements, so
   *    `TuiDropdownOpen.onClick` on the textfield host never fires. We
   *    dispatch `inputEl.click()` to reproduce the bubbling click.
   *
   * 2. **Datepicker** (`tuiInputDate` / `tuiInputDateRange`, e.g.
   *    `gatewee-datepicker-v2`, `gatewee-datepicker-range`): Taiga UI's
   *    `TuiInputDateBase.onClick()` is explicitly gated by `!this.mobile`
   *    and is a no-op on iPhone. We dispatch a synthetic `pointerdown` on
   *    `<tui-textfield>` inside `zone.run()`, which triggers
   *    `TuiTextfieldBaseComponent.onIconClick()` — the shared entry-point
   *    that sets `this.open` and opens the calendar dropdown.
   *
   * 3. **Plain editable input** (e.g. `gatewee-input-v2`): focus it so the
   *    iOS keyboard appears (original behaviour, unchanged).
   *
   * Guards:
   * - Only fires when the tap lands directly on the inner `<input>` /
   *   `<textarea>` (`directInputTap`). Taps on the textfield host, chevron
   *   pseudo-element, or calendar icon already produce native events handled
   *   by Taiga UI, and synthesizing additional ones would double-toggle.
   * - The listener is passive and runs outside the Angular zone, so it has
   *   no scroll or change-detection overhead.
   */
  private installIosInputFocusFix(): void {
    // This workaround compensates for iOS Safari not synthesizing click
    // events on readonly inputs nested in custom elements. Android and desktop
    // browsers do synthesize the click, so running here would double-toggle
    // the dropdown (open then immediately close). iPadOS 13+ reports MacIntel.
    const isIos =
      /iphone|ipad|ipod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (!isIos) return;

    this.zone.runOutsideAngular(() => {
      document.addEventListener(
        'touchend',
        (ev: Event) => {
          const target = ev.target as HTMLElement | null;
          if (!target) return;

          let inputEl: HTMLInputElement | HTMLTextAreaElement | null = null;
          let directInputTap = false;

          if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
            inputEl = target as HTMLInputElement | HTMLTextAreaElement;
            directInputTap = true;
          } else {
            const host = target.closest('tui-textfield, tui-textarea');
            if (host) {
              inputEl = host.querySelector('input, textarea') as
                | HTMLInputElement
                | HTMLTextAreaElement
                | null;
            }
          }

          if (!inputEl || inputEl.disabled) return;

          const field = inputEl.closest('tui-textfield, tui-textarea');
          if (!field) return;

          // Only compensate for direct taps on the inner input. Taps on the
          // textfield host / icon / chevron already produce native events.
          if (!directInputTap) return;

          const isSelectLike =
            inputEl.readOnly || inputEl.getAttribute('inputmode') === 'none';
          const isDatepicker =
            inputEl.hasAttribute('tuiInputDate') ||
            inputEl.hasAttribute('tuiInputDateRange');

          if (isSelectLike) {
            // iOS skips the synthetic click for nested readonly inputs.
            // Dispatch one so TuiDropdownOpen.onClick can toggle the dropdown.
            inputEl.click();
          } else if (isDatepicker) {
            // TuiInputDateBase.onClick is a no-op on mobile (guarded by
            // !this.mobile). Dispatch pointerdown on <tui-textfield> inside
            // zone.run() to trigger onIconClick(), which sets this.open and
            // opens the calendar.
            this.zone.run(() => {
              (field as HTMLElement).dispatchEvent(
                new PointerEvent('pointerdown', {cancelable: true}),
              );
            });
          } else if (document.activeElement !== inputEl) {
            // Plain editable input: focus it so the iOS keyboard appears.
            inputEl.focus({preventScroll: true});
          }
        },
        {capture: false, passive: true},
      );
    });
  }

  private setupLanguage() {
    const browserLang = navigator.language.split('-')[0];
    const defaultLang = ['en', 'pt', 'es', 'zh'].includes(browserLang)
      ? browserLang
      : 'pt';

    this.translate.addLangs(['en', 'pt', 'es', 'zh']);
    this.translate.setDefaultLang('pt');
    this.translate.use(defaultLang);
  }

  private loadGoogleAnalytics(gaId: string) {
    if (!document.getElementById('ga-script')) {
      const script = document.createElement('script');
      script.id = 'ga-script';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(script);
    }
  }

  setSessionUser(user: RecipientUser): void {
    if (Object.values(user).length > 0) {
      return sessionStorage.setItem(
        'sessionUser',
        JSON.stringify({
          id: user?.id,
          email: user?.email,
          origin: 'recipient',
          name: user?.name,
          companyName: user?.recipient?.name,
          companyDocument: user?.recipient?.document,
          profilePhoto: user?.profilePhoto,
        }),
      );
    }
  }

  logRocketInit(): void {
    const user = JSON.parse(sessionStorage.getItem('sessionUser') || '{}');
    LogRocket.init('kwim3g/gatewee-web-recipient');

    if (user) {
      this.logRocketIdentify(user);
    }

    window.addEventListener('storage', event => {
      if (event.storageArea === sessionStorage && event.key === 'sessionUser') {
        const user = JSON.parse(event.newValue || '{}');
        if (user) {
          this.logRocketIdentify(user);
        }
      }
    });
  }

  logRocketIdentify(user: SessionUser): void {
    return LogRocket.identify(
      user.id?.toString() ?? '',
      user as unknown as Record<string, string | number | boolean>,
    );
  }
}
